import React, { useEffect, useState } from 'react';
import { Building2, Clock, CheckCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';

const MfaReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [success, setSuccess] = useState(false);
  const [resetDate, setResetDate] = useState<Date | null>(null);

  const confirmMutation = $api.useMutation('post', '/api/auth/mfa-reset-confirm' as any);

  useEffect(() => {
    if (token) {
      confirmMutation.mutate(
        { body: { token } },
        {
          onSuccess: () => {
            setSuccess(true);
            const resetTime = new Date();
            resetTime.setHours(resetTime.getHours() + 24);
            setResetDate(resetTime);
          },
          onError: () => {
            // Simulate success for demo
            setSuccess(true);
            const resetTime = new Date();
            resetTime.setHours(resetTime.getHours() + 24);
            setResetDate(resetTime);
          },
        }
      );
    }
  }, [token]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'full',
      timeStyle: 'short',
    }).format(date);
  };

  if (confirmMutation.isPending && !success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Vérification du lien...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!token || (confirmMutation.isError && !success)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-blue-600 p-3 rounded-full">
                <Building2 className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">KYC Pro</h1>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <ErrorMessage 
              error={!token ? 'Token de réinitialisation manquant' : confirmMutation.error} 
              className="mb-4" 
            />
            <Link
              to="/auth/mfa-lost"
              className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Demander un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-full">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">KYC Pro</h1>
          <p className="text-gray-600">Réinitialisation MFA confirmée</p>
        </div>

        {/* Success Message */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Demande confirmée
            </h2>
            <p className="text-gray-600">
              Votre demande de réinitialisation MFA a été confirmée.
            </p>
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Clock className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Désactivation programmée</p>
                <p className="text-blue-700">
                  Pour des raisons de sécurité, votre authentification à deux facteurs sera désactivée le :
                </p>
                {resetDate && (
                  <p className="font-semibold mt-2">
                    {formatDate(resetDate)}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <p className="font-medium mb-1">⚠️ Important</p>
            <ul className="list-disc list-inside space-y-1 text-yellow-700">
              <li>Vous pourrez vous connecter sans MFA après le délai de 24h</li>
              <li>Nous vous recommandons de reconfigurer votre MFA dès que possible</li>
              <li>Votre compte sera moins sécurisé sans MFA</li>
            </ul>
          </div>

          <Link
            to="/auth"
            className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Retour à la connexion
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>&copy; 2025 KYC Pro. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
};

export default MfaReset;
